export interface ChangelogItem {
  id: string;
  title: string;
  lines: string[];
}

export interface ChangelogSection {
  id: string;
  title: string;
  items: ChangelogItem[];
}

export interface ChangelogVersion {
  version: string;
  date: string;
  sections: ChangelogSection[];
}

const VERSION_BLOCK_REGEX = /^##\s*Versão\s+([\d.]+)\s*\|\s*(.+?)\s*##\s*$/m;

const CHANGELOG_MONTHS: Record<string, number> = {
  janeiro: 0,
  fevereiro: 1,
  março: 2,
  marco: 2,
  abril: 3,
  maio: 4,
  junho: 5,
  julho: 6,
  agosto: 7,
  setembro: 8,
  outubro: 9,
  novembro: 10,
  dezembro: 11
};

export function parseChangelogDate(dateStr: string): Date | null {
  const match = dateStr.trim().match(/^(\d{1,2})\s+de\s+([a-zçãéó]+)\s+de\s+(\d{4})$/i);
  if (!match) return null;

  const day = Number(match[1]);
  const month = CHANGELOG_MONTHS[match[2].toLowerCase()];
  const year = Number(match[3]);

  if (month === undefined || Number.isNaN(day) || Number.isNaN(year)) return null;

  return new Date(year, month, day);
}

export function isReleaseWithinDays(
  releaseDate: Date,
  days: number,
  referenceDate = new Date()
): boolean {
  const release = new Date(releaseDate.getFullYear(), releaseDate.getMonth(), releaseDate.getDate());
  const reference = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate()
  );
  const diffDays = (reference.getTime() - release.getTime()) / (1000 * 60 * 60 * 24);

  return diffDays >= 0 && diffDays < days;
}

export function isRecentChangelogRelease(
  dateStr: string,
  days = 7,
  referenceDate = new Date()
): boolean {
  const releaseDate = parseChangelogDate(dateStr);
  if (!releaseDate) return false;

  return isReleaseWithinDays(releaseDate, days, referenceDate);
}

function parseSectionItems(text: string, sectionId: string): ChangelogItem[] {
  const items: ChangelogItem[] = [];
  const lines = text.split('\n');
  let current: ChangelogItem | null = null;

  lines.forEach((line) => {
    const topLevelMatch = line.match(/^-\s+\[(.+?)\]/);
    if (topLevelMatch) {
      if (current) items.push(current);
      const title = topLevelMatch[1];
      current = {
        id: `${sectionId}-${items.length}-${title}`,
        title,
        lines: []
      };
      const rest = line.replace(/^-\s+\[.+?\]\s*/, '').trim();
      if (rest) current.lines.push(rest);
      return;
    }

    if (current && line.trim()) {
      current.lines.push(line.trim());
    }
  });

  if (current) items.push(current);
  return items;
}

function parseVersionBlock(version: string, date: string, block: string): ChangelogVersion {
  const body = block.replace(VERSION_BLOCK_REGEX, '').trim();
  const sections: ChangelogSection[] = [];

  body.split(/(?=^###\s+)/m).forEach((part, sectionIndex) => {
    const trimmed = part.trim();
    if (!trimmed.startsWith('###')) return;

    const sectionLines = trimmed.split('\n');
    const title = sectionLines[0].replace(/^###\s+/, '').trim();
    const sectionId = `${version}-${sectionIndex}-${title}`;
    const items = parseSectionItems(sectionLines.slice(1).join('\n'), sectionId);

    if (title || items.length) {
      sections.push({ id: sectionId, title, items });
    }
  });

  return { version, date, sections };
}

export function splitChangelogBlocks(content: string): string[] {
  return content
    .split(/(?=^##\s*Versão\s+)/m)
    .map((block) => block.trim())
    .filter((block) => VERSION_BLOCK_REGEX.test(block));
}

export function parseChangelogBlock(block: string): ChangelogVersion | null {
  const headerMatch = block.match(VERSION_BLOCK_REGEX);
  if (!headerMatch) return null;

  const [, version, date] = headerMatch;
  return parseVersionBlock(version, date.trim(), block);
}

export function parseChangelogVersion(content: string, targetVersion: string): ChangelogVersion | null {
  const matchingBlock = splitChangelogBlocks(content).find((block) => {
    const headerMatch = block.match(VERSION_BLOCK_REGEX);
    return Boolean(headerMatch && headerMatch[1] === targetVersion);
  });

  if (!matchingBlock) return null;

  return parseChangelogBlock(matchingBlock);
}
